import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import './Home.css';
import PyramidChart from "./../charts/PyramidChart";
import axios from 'axios';
import ClipLoader from 'react-spinners/ClipLoader';
import Dashboard from '../components/Dashboard.js';
import PieChartSection from '../components/PieChartSection.js';
import CONFIG from '../config.js';

const Home = () => {
    const { user, fetchData, setFetchData, isAdmin, adminSelectedUser } = useContext(AuthContext);
    let userInfo = user;
    if (isAdmin) {
        userInfo = adminSelectedUser;
    }
    const [loading, setLoading] = useState(true);
    const [pieChartDataLoading, setPieChartDataLoading] = useState(true);
    const [hrvBreakdownData, setHrvBreakdownData] = useState();
    const [userDisplayInfo, setUserDisplayInfo] = useState();
    const [dailyHrvData, setDailyHrvData] = useState();
    const [weeklyHrvData, setWeeklyHrvData] = useState();
    const [monthlyHrvData, setMonthlyHrvData] = useState();
    const [quarterlyHrvData, setQuarterlyHrvData] = useState();
    const [predictionsQuarterlyData, setPredictionsQuarterlyData] = useState();

    const pieChartDataFormat = {
        'weekly': {
            'data': [null, null],
            'text': ''
        },
        'monthly': {
            'data': [null, null],
            'text': ''
        },
        'quarterly': {
            'data': [null, null],
            'text': ''
        }
    };
    const [weekdayData, setWeekdayData] = useState(pieChartDataFormat);
    const [weekendData, setWeekendData] = useState(pieChartDataFormat);
    const [weekData, setWeekData] = useState(pieChartDataFormat);

    function formatDate(dateString, daysToSubtract) {
        const [year, month, day] = dateString.split("-").map(Number);
        let newDay = day - daysToSubtract;
        let newMonth = month;
        let newYear = year;
    
        // Days in each month (ignoring leap years for now)
        const daysInMonth = [31, (newYear % 4 === 0 && newYear % 100 !== 0) || newYear % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    
        // Adjust for month and year change
        while (newDay <= 0) {
            newMonth--; // Move to the previous month
            if (newMonth === 0) {
                newMonth = 12; // Wrap around to December
                newYear--; // Decrease the year
            }
            newDay += daysInMonth[newMonth - 1]; // Add the days of the previous month
        }
    
        // Format back to "YYYY-MM-DD"
        return `${newYear}-${String(newMonth).padStart(2, "0")}-${String(newDay).padStart(2, "0")}`;
    }

    const url = CONFIG.createUrl();

    const config = {headers: CONFIG.HEADERS};

    useEffect(() => {
        const fetchHRVBreakdownData = async () => {
            try {
                const maxDateResponse = await axios.get(`${url}hrv/predictions/max-day?uuid=${userInfo.uuid}`, config);
                setFetchData(prev => ({ ...prev, maxDate: maxDateResponse.data.max_day }));

                const weekStartDate = formatDate(maxDateResponse.data.max_day, 6);
                const monthStartDate = formatDate(maxDateResponse.data.max_day, 29);
                const quarterStartDate = formatDate(maxDateResponse.data.max_day, 89);

                const [weeklyDashboardResponse, monthlyDashboardResponse, quarterlyDashboardResponse, dailyDashboardResponse, pyramidChartPredictionQuarterlyData, userDisplayInfoResponse] = await axios.all([
                    axios.get(`${url}dashboard/metrics/hr_hrv_breakdown?uuid=${userInfo.uuid}&start_date=${weekStartDate}&end_date=${maxDateResponse.data.max_day}`, config),
                    axios.get(`${url}dashboard/metrics/hr_hrv_breakdown?uuid=${userInfo.uuid}&start_date=${monthStartDate}&end_date=${maxDateResponse.data.max_day}`, config),
                    axios.get(`${url}dashboard/metrics/hr_hrv_breakdown?uuid=${userInfo.uuid}&start_date=${quarterStartDate}&end_date=${maxDateResponse.data.max_day}`, config),
                    axios.get(`${url}dashboard/metrics/hr_hrv_breakdown?uuid=${userInfo.uuid}&start_date=${maxDateResponse.data.max_day}&end_date=${maxDateResponse.data.max_day}`, config),
                    axios.get(`${url}hrv/predictions?uuid=${userInfo.uuid}&eval_date=${maxDateResponse.data.max_day}&start_date=${quarterStartDate}&end_date=${formatDate(maxDateResponse.data.max_day, 1)}`, config),
                    axios.get(`${url}user/profile?email=${userInfo.email}`, config)
                ]);
                const dashboardData = {
                    weeklyHrvData: weeklyDashboardResponse.data,
                    monthlyHrvData: monthlyDashboardResponse.data,
                    quarterlyHrvData: quarterlyDashboardResponse.data,
                    dailyHrvData: dailyDashboardResponse.data,
                    predictionsQuarterlyData: pyramidChartPredictionQuarterlyData.data
                }
                addDashboardData(weeklyDashboardResponse.data, monthlyDashboardResponse.data, quarterlyDashboardResponse.data, dailyDashboardResponse.data, pyramidChartPredictionQuarterlyData.data, userDisplayInfoResponse.data);
                setFetchData(prev => ({ ...prev, dashboardData: dashboardData, userDisplayInfo: userDisplayInfoResponse.data }));
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        };
        if (!fetchData.dashboardData || !fetchData.userDisplayInfo) {
            fetchHRVBreakdownData();
        } else {
            addDashboardData(fetchData.dashboardData.weeklyHrvData, fetchData.dashboardData.monthlyHrvData, fetchData.dashboardData.quarterlyHrvData, fetchData.dashboardData.dailyHrvData, fetchData.dashboardData.predictionsQuarterlyData, fetchData.userDisplayInfo);
        }
    }, []);

    const addDashboardData = (weeklyHrv, monthlyHrv, quarterlyHrv, dailyHrv, predictionsQuarterlyData, userDisplay) => {
        setHrvBreakdownData(weeklyHrv);
        setWeeklyHrvData(weeklyHrv);
        setMonthlyHrvData(monthlyHrv);
        setQuarterlyHrvData(quarterlyHrv)
        setDailyHrvData(dailyHrv);
        setPredictionsQuarterlyData(predictionsQuarterlyData);
        setUserDisplayInfo(userDisplay);
        setLoading(false);
    }

    useEffect(() => {
        const fetchTopPercentileHRV = async () => {
            try {
                const top_percentile = 25;
                let maxDate = '';
                if (fetchData.maxDate) {
                    maxDate = fetchData.maxDate;
                } else {
                    const maxDateResponse = await axios.get(`${url}hrv/predictions/max-day?uuid=${userInfo.uuid}`, config);
                    maxDate = maxDateResponse.data.max_day;
                }
                const weekStartDate = formatDate(maxDate, 6);
                const monthStartDate = formatDate(maxDate, 29);
                const quarterStartDate = formatDate(maxDate, 89);

                const [pieChartWeeklyResponse, pieChartMonthlyResponse, pieChartQuarterlyResponse] = await axios.all([
                    axios.get(`${url}dashboard/metrics/top_percentile_hrv?uuid=${userInfo.uuid}&start_date=${weekStartDate}&end_date=${maxDate}&top_percentile=${top_percentile}`, config),
                    axios.get(`${url}dashboard/metrics/top_percentile_hrv?uuid=${userInfo.uuid}&start_date=${monthStartDate}&end_date=${maxDate}&top_percentile=${top_percentile}`, config),
                    axios.get(`${url}dashboard/metrics/top_percentile_hrv?uuid=${userInfo.uuid}&start_date=${quarterStartDate}&end_date=${maxDate}&top_percentile=${top_percentile}`, config)
                ]);

                const newWeekData = createPieChartData(pieChartWeeklyResponse.data.week, pieChartMonthlyResponse.data.week, pieChartQuarterlyResponse.data.week);
                const newWeekDayData = createPieChartData(pieChartWeeklyResponse.data.weekdays, pieChartMonthlyResponse.data.weekdays, pieChartQuarterlyResponse.data.weekdays);
                const newWeekendData = createPieChartData(pieChartWeeklyResponse.data.weekends, pieChartMonthlyResponse.data.weekends, pieChartQuarterlyResponse.data.weekends);

                const pieChartData = {
                    weekData: newWeekData,
                    weekdayData: newWeekDayData,
                    weekendData: newWeekendData
                }
                setFetchData(prev => ({ ...prev, pieChartData: pieChartData, weekStartDate: weekStartDate, monthStartDate: monthStartDate, quarterStartDate: quarterStartDate }));
                addPieChartData(newWeekData, newWeekDayData, newWeekendData);
            } catch (err) {
                console.log(err);
            } finally {
                setPieChartDataLoading(false);
            }
        };
        if (!fetchData.pieChartData) {
            fetchTopPercentileHRV();
        } else {
            addPieChartData(fetchData.pieChartData.weekData, fetchData.pieChartData.weekdayData, fetchData.pieChartData.weekendData);
        }
    }, []);

    const createPieChartData = (weeklyData, monthlyData, quarterlyData) => {
        return {
            'weekly': {
                'data': [weeklyData.percentage_in_top_percentile.toFixed(0), weeklyData.percentage_not_in_top_percentile.toFixed(0)],
                'text': weeklyData.text
            },
            'monthly': {
                'data': [monthlyData.percentage_in_top_percentile.toFixed(0), monthlyData.percentage_not_in_top_percentile.toFixed(0)],
                'text': monthlyData.text
            },
            'quarterly': {
                'data': [quarterlyData.percentage_in_top_percentile.toFixed(0), quarterlyData.percentage_not_in_top_percentile.toFixed(0)],
                'text': quarterlyData.text
            }
        };
    }

    const addPieChartData = (weekData, weekDayData, weekendData) => {
        setWeekData(weekData);
        setWeekdayData(weekDayData);
        setWeekendData(weekendData);
        setPieChartDataLoading(false);
    }

    return (
        <div className="container">
            <div className="main-content">
                <section className="card">
                    <div>
                        <h2 className="welcome-text">Welcome,
                            {userDisplayInfo &&
                                <span>&nbsp; {userDisplayInfo.first_name} {userDisplayInfo.last_name}</span>
                            }
                            !</h2>
                    </div>
                    <div className="text-container disclaimer-text">
                        <p><b>Disclaimer:</b> The information presented on this platform is not intended to prevent, diagnose, treat, or cure any disease or medical condition. The insights and suggestions provided are derived from an analysis of your data using advanced machine learning models, combined with expert opinions. It is the user's sole responsibility to consult with a medical professional to determine if the recommended actions are appropriate for them. Heart Breath Mind cannot be held responsible for any omissions or inadvertent errors that occurred during data collection, interpretation, or presentation.</p>

                        <p><b>Note:</b> The insights and suggestions offered by this platform are the result of comprehensive data analysis and expert review. Our proprietary algorithms process your data to generate personalized recommendations, which are then validated by experts to ensure their relevance. These insights are meant to guide your efforts towards improving your nocturnal HRV and overall well-being, rather than serving as a definitive representation of your current or future health status.</p>
                    </div>
                    {(loading || pieChartDataLoading) &&
                        <div className="spinner-container">
                            <ClipLoader color={"#123abc"} loading={loading} size={50} />
                        </div>
                    }
                    {!loading && !pieChartDataLoading && hrvBreakdownData && dailyHrvData &&
                        <div className="metrics-container">
                            <Dashboard user={userInfo} dailyHrvData={dailyHrvData} weeklyHrvData={weeklyHrvData} monthlyHrvData={monthlyHrvData} quarterlyHrvData={quarterlyHrvData} maxDateVal={fetchData.maxDate} />

                            <hr className="divider" />

                            <PyramidChart predictionsQuarterlyData={predictionsQuarterlyData} />

                            <hr className="divider" />

                            <PieChartSection weekData={weekData} weekdayData={weekdayData} weekendData={weekendData} />
                        </div>
                    }
                </section>
            </div>
        </div>
    );

};

export default Home;
